import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

import { NAV_GROUPS } from '../core/nav/nav-config';
import { UserService } from '../core/services/user.service';

/**
 * adminUsersRoleGuard — QA SEC-01 (Srikanth, Dev, 2026-10-13).
 *
 * DEFECT: the /admin/users route only carried `authGuard`. The Users nav item is hidden for
 * non-admins, but a SCHOOL_USER who navigated to /admin/users by direct URL still got the page:
 * the API correctly answered 403, yet UsersStateService collapsed every error into a generic
 * "We can't load users right now" message, so the console frame, filters and the "Invite user"
 * CTA all rendered.
 *
 * FIX: a role-aware guard composed AFTER authGuard (which proves the Keycloak session first).
 * Roles outside the org-admin set get a clean snackbar and a /dashboard UrlTree — the same
 * pattern as uploadRoleGuard.
 *
 * Notes:
 *  - The server-side RequireOrgAdmin policy + UserAdminScopeCheck STAY the authorization
 *    boundary on every /api/admin/users/* call. This guard is UX only; it is not, and must not
 *    be treated as, the authorization boundary.
 *  - ADR-0031: roles are API-sourced, so the guard is ASYNC and awaits the memoized
 *    GET /api/users/me bootstrap. Without that await a hard refresh straight onto /admin/users
 *    would evaluate roles while the profile is still in flight ([] roles) and false-deny an
 *    allowed admin. ensureLoaded() is single-flight, so this costs no extra round trip once loaded.
 *  - ALL inject() calls happen BEFORE the first await: Angular's DI context is lost after an
 *    await inside a functional guard.
 */

/** Roles permitted to reach /admin/users. DERIVED from the Users nav item in nav-config so the
 *  nav visibility and the route gate cannot drift. Falls back to an empty list (fail closed) if
 *  the nav item is ever removed; the spec locks the four org-admin roles. */
export const ADMIN_USERS_ROLES: readonly string[] =
  NAV_GROUPS.find((item) => item.route === '/admin/users')?.roles ?? [];

export const adminUsersRoleGuard: CanActivateFn = async () => {
  const user = inject(UserService);
  const router = inject(Router);
  const snackBar = inject(MatSnackBar);

  await user.ensureLoaded();

  // hasAnyRole is case-insensitive and treats empty/unloaded roles as NOT authorized (ADR-0031).
  if (user.hasAnyRole(...ADMIN_USERS_ROLES)) {
    return true;
  }

  snackBar.open("You don't have access to this page.", 'Dismiss', {
    duration: 8000,
    politeness: 'polite',
  });
  return router.parseUrl('/dashboard');
};
